(ns app.front.view.group.admin.subs
  (:require [app.domain.booking.model :as booking-model]
            [app.domain.form.schema :as form-schema]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.view.attachment.common :as attachment-common]
            [app.front.view.group.admin.core :as core]
            [app.front.view.group.subs :as group-subs]
            [app.front.view.inspections.subs :as inspection-sub]
            [app.i18n.model :as i18n-model]
            [app.reporting.model :as reporting-model]
            [app.rich-text :as rt]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [core.front.comm :as comm]
            [core.front.config :as front-config]
            [core.front.feature-flags :as ff]
            [core.front.i18n :as i18n]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(def permit-type-config-editable-keys
  [:permit-type-descriptions
   :group-config-settings])

(rf/reg-sub ::user-management-route
  ;; The group user management the flag links to; both stay routable.
  :<- [::ff/enabled? :feature-flag.id/new-group-user-management]
  (fn [new-user-management? _]
    (if new-user-management?
      :view/group.admin.users
      :view/group.admin.user-management-view)))

(defn group-admin-route? [route-name]
  (when-let [route-str (some-> route-name name)]
    (or (str/starts-with? route-str "group.admin")
        (str/starts-with? route-str "group.reporting"))))

(def route-support-aliases
  "A route is also supported where its alias is among the group's management views.
  The flag `:feature-flag.id/new-group-user-management` decides which user management
  the views list, and both stay routable."
  {:view/group.admin.system-email-template :view/group.admin.templates
   :view/group.admin.stamp.new             :view/group.admin.templates
   :view/group.admin.stamp.edit            :view/group.admin.templates
   :view/group.admin.users                 :view/group.admin.user-management-view
   :view/group.admin.user-management-view  :view/group.admin.users})

;;; Events
;;;;;;;;;;

(rf/reg-event-db ::set-group-id
  (fn [db [_ group-id]]
    (-> db
        (assoc ::group-id group-id))))

;; When the group changes, ensure the current route is still supported by the
;; user's management-views for that group. If not, redirect to first viable view
;; before any view-specific requests are made.
(rf/reg-event-fx ::set-group-id-and-route
  (fn [{:keys [db]} [_ group-id route-name]]
    (let [db                   (assoc db ::group-id group-id)
          user                 (:session/user db)
          all-groups           (concat (:user/authority-groups user)
                                       (:user/administered-groups user)
                                       (:user/domain-groups user)
                                       (:user/superdomain-groups user))
          group                (p/find-first all-groups {:db/id group-id})
          views                (:management-views group)
          ;; reporting views have different base views, reporting route is supported if any reporting view available
          reporting-route?     (-> reporting-model/reporting-views set (contains? route-name))
          reporting-supported? (and reporting-route?
                                    (some (set reporting-model/reporting-views) views))
          ;; Only navigate if we're on a group-admin route
          ;; Otherwise just set the group-id (for workspace, dashboard, etc.)
          supported?           (some (set views) [route-name (route-support-aliases route-name)])
          target               (when (and route-name (group-admin-route? route-name))
                                 (if (and group
                                          (seq views)
                                          (not supported?)
                                          (not reporting-supported?))
                                   ;; Current route not supported by group, redirect to first view
                                   (first views)
                                   ;; Current route is supported, stay on it
                                   route-name))]
      (cond-> {:db db}
        target (assoc :dispatch [:route/push {:route  target
                                              :params {:group-id group-id}}])))))

;;; Subscriptions
;;;;;;;;;;;;;;;;;

(rf/reg-sub ::group-id
  :-> ::group-id)

(rf/reg-sub ::group
  :<- [::group-id]
  :<- [::entities/context]
  (fn [[group-id ctx] _]
    (->> group-id
         (entities/find-entity ctx))))

(rf/reg-sub ::superdomain
  :<- [::group]
  (fn [group _]
    (when-not (group-model/superdomain? group)
      (group-model/group-superdomain group))))

(rf/reg-sub ::superdomain-id
  :<- [::superdomain]
  :-> :db/id)

(rf/reg-sub ::group-in-superdomain?
  :<- [::superdomain]
  (fn [superdomain _]
    (not (nil? superdomain))))

(rf/reg-sub ::use-superdomain-calendar?
  :<- [::group]
  :<- [::group-in-superdomain?]
  (fn [[group in-superdomain?] _]
    (->> (when in-superdomain?
           (:group/use-superdomain-calendar? group))
         true?)))

(rf/reg-sub ::auth-group?
  :<- [::group]
  :-> group-model/application-or-upper-tier-authority?)


(rf/reg-sub ::superdomain-group?
  :<- [::group]
  :-> group-model/superdomain?)

(rf/reg-sub ::group-permit-types
  :<- [::group]
  :-> group-model/get-permit-types)

(rf/reg-sub ::group-primary-permit-types
  :<- [::group-permit-types]
  :-> (partial filter ptc-model/primary-permit-types))

(rf/reg-sub ::group-primary-permit-type
  :<- [::group]
  :-> group-model/primary-permit-type)

(rf/reg-sub ::group-property-i18n-mapping
  :<- [::group]
  :-> group-model/get-i18n-mapping)

(defn superdomain-execution-tr-key [permit-types]
  (let [execution-permit-types (set/intersection permit-types form-schema/permit-types-with-execution-form)
        code-enforcement?      (contains? execution-permit-types :permit-type/CE)
        work-phase?            (seq (disj execution-permit-types :permit-type/CE))]
    (cond
      (and code-enforcement? work-phase?) :form.schema.type/execution-work-phase-CE
      code-enforcement?                  :form.schema.type/execution-CE
      :else                              :form.schema.type/execution)))

(defn group-tr [[tr superdomain? permit-types property-i18n-mapping]]
  ;; Only update when the permit types or property mapping change, and use a
  ;; combined execution label for Super Domains rather than product-specific translations.
  (i18n-model/wrap-tr-fn
    (merge property-i18n-mapping
           (if superdomain?
             {:form.schema.type/execution (superdomain-execution-tr-key permit-types)}
             (i18n-model/get-permit-type-specific-tr-keys permit-types)))
    tr))

(rf/reg-sub ::group-tr
  :<- [:tr]
  :<- [::superdomain-group?]
  :<- [::group-permit-types]
  :<- [::group-property-i18n-mapping]
  :-> group-tr)

(rf/reg-sub ::group-tr-quiet
  :<- [:tr/quiet]
  :<- [::superdomain-group?]
  :<- [::group-permit-types]
  :<- [::group-property-i18n-mapping]
  :-> group-tr)

(rf/reg-sub ::group-tz
  :<- [::group]
  :-> :group/tz)

(rf/reg-sub ::group-name
  :<- [::group]
  :-> :group/name)

(rf/reg-sub ::group-type
  :<- [::group]
  :-> :group/type)

(rf/reg-sub ::group-features
  :<- [::group]
  :-> :group/features)

(rf/reg-sub ::currency
  :<- [::group]
  :-> :group/currency)

(rf/reg-sub ::parent-group-features
  :<- [::group]
  (fn [group _]
    (-> group :entity/parent :group/features)))

(rf/reg-sub ::descendant-authority-groups
  :<- [::group]
  :-> group-model/descendant-authority-groups)

(rf/reg-sub ::descendant-authority-group-features
  :<- [::descendant-authority-groups]
  (fn [descendant-authority-groups _]
    (->> descendant-authority-groups
         (mapcat :group/features)
         set)))

(rf/reg-sub ::descendant-authority-group-ids
  :<- [::descendant-authority-groups]
  (fn [descendant-authority-groups _]
    (->> descendant-authority-groups
         (keep :db/id)
         (into #{}))))

(rf/reg-sub ::has-feature?
  :<- [::group-features]
  (fn [features [_ feature]]
    (contains? (set features) feature)))

(rf/reg-sub ::parent-has-feature?
  :<- [::parent-group-features]
  (fn [features [_ feature]]
    (contains? (set features) feature)))


(rf/reg-sub ::group-or-descendant-has-feature?
  :<- [::group-features]
  :<- [::descendant-authority-group-features]
  (fn [[group-features descendant-features] [_ feature]]
    (or (contains? (set group-features) feature)
        (contains? descendant-features feature))))

(rf/reg-sub ::entities-for-group
  :<- [::group-id]
  :<- [::entities/context]
  group-subs/handle-entities-for-group)

(rf/reg-sub ::group-inherited-template-ids
  :<- [::group]
  (fn [group _]
    (->> group
         :group/enabled-inherited-templates
         (map :db/id)
         set)))

(rf/reg-sub ::template-kind-entities
  :<- [::entities-for-group]
  inspection-sub/handle-template-kind-entities)

(rf/reg-sub ::group-availability
  :<- [::group]
  :<- [::superdomain]
  :<- [::use-superdomain-calendar?]
  (fn [[group superdomain use-superdomain-calendar?] _]
    (if use-superdomain-calendar?
      (booking-model/group-availability superdomain)
      (booking-model/group-availability group))))

(rf/reg-sub ::ecm-integration-enabled?
  :<- [::has-feature? :group.feature/ecm-integration]
  :<- [::group]
  (fn [[feature-enabled? {:group/keys [ecm-integration-config]}] _]
    (and feature-enabled?
         (-> ecm-integration-config :integration-config/service some?))))

(rf/reg-sub ::backend-system-name
  :<- [::group]
  :<- [::group-features]
  :<- [:tr]
  (fn [[group features tr] [_ fallback-key]]
    (if-let [group-id (and (or (contains? features :group.feature/backend-system-integration)
                               fallback-key)
                           (-> group :group/id))]
      (group-model/backend-system-name tr group-id fallback-key)
      "")))

(rf/reg-sub ::attachment-types
  :<- [::group-id]
  :<- [::entities/context]
  (fn [[group-id ctx]]
    (entities/find-entities ctx
                            {:attachment-type/authority-group group-id})))

(rf/reg-sub ::attachment-types-with-i18n
  :<- [::attachment-types]
  :<- [:language]
  (fn [[attachment-types lang] _]
    (->> attachment-types
         (map (juxt #(attachment-common/attachment-type-label % lang) :db/id))
         (sort-by first))))

(rf/reg-sub ::domain-available-attachment-types-with-i18n
  :<- [::domain-attachment-types]
  :<- [::available-attachment-types]
  :<- [:language]
  (fn [[attachment-types available-attachment-types lang] _]
    (let [attachment-types (filter #(contains? available-attachment-types (:db/id %)) attachment-types)
          labels           (map #(attachment-common/attachment-type-label % lang) attachment-types)
          duplicate-labels (->> labels
                                frequencies
                                (keep (fn [[label cnt]] (when (> cnt 1) label)))
                                set)]
      (->> attachment-types
           (map (fn [at]
                  (let [label (attachment-common/attachment-type-label at lang)]
                    [(if (contains? duplicate-labels label)
                       (str label " (" (-> at :attachment-type/authority-group :group/name) ")")
                       label)
                     (:db/id at)])))
           (sort-by first)))))

(rf/reg-sub ::available-attachment-types
  :<- [::config-defaults]
  #(:available-attachment-types % #{}))

(rf/reg-sub ::root-group-config
  :<- [::group-id]
  :<- [::entities/context]
  (fn [[group-id ctx] [_ permit-type]]
    (if permit-type
      (entities/find-entity ctx {:group.config/target permit-type
                                 :group.config/group  group-id})
      (js/console.error "Missing permit-type in ::root-group-config sub"))))

(rf/reg-sub ::root-group-configs
  :<- [::group]
  :<- [::entities/context]
  :<- [::group-permit-types]
  :<- [::superdomain-group?]
  (fn [[group ctx permit-types superdomain?] _]
    (let [groups (if superdomain?
                   (->> group
                        group-model/descendant-authority-groups
                        set)
                   #{group})]
      (->> (entities/find-entities ctx {:group.config/level :group-config.level/permit-type
                                        :domain/state       :group-config.state/enabled})
           (filter #(contains? groups (:group.config/group %)))
           (filter #(contains? permit-types (:group.config/target %)))
           (sort-by :group.config/target)))))

(rf/reg-sub ::category-group-configs
  (fn [[_ permit-type]]
    [(rf/subscribe [::root-group-config permit-type])
     (rf/subscribe [:language])])
  (fn [[root-config language] _]
    (->> root-config
         :entity/_parent
         (filter (du/in-state-fn :group-config.state/enabled
                                 :group-config.state/disabled))
         (config-model/sort-group-configs language))))

(rf/reg-sub ::child-group-configs-sorted
  (fn [[_ parent-config-id]]
    [(rf/subscribe [::entities/entities {:entity/parent parent-config-id
                                         :entity/type   :entity.type/group-config}])
     (rf/subscribe [:language])])
  (fn [[group-configs language] [_ _ only-in-states]]
    (->> (config-model/sort-group-configs language group-configs)
         (filter #(contains? (or only-in-states #{:group-config.state/enabled}) (:domain/state %))))))

(rf/reg-sub ::work-types-with-targets-exist?
  (fn [[_ category-config-id]]
    (rf/subscribe [::entities/entities {:entity/parent category-config-id
                                        :entity/type   :entity.type/group-config}]))
  (fn [work-type-configs _]
    (->> work-type-configs
         (keep (comp seq :entity/_parent))
         seq
         boolean)))

(rf/reg-sub ::group-config-level
  (fn [[_ group-config-id]]
    (rf/subscribe [::entities/entity group-config-id]))
  :-> :group.config/level)

(rf/reg-sub ::editable-verdict-images
  (fn [[_ authority-group-id]]
    (rf/subscribe [::entities/entities {:file/category          :file.category/verdict-image
                                        :domain/authority-group authority-group-id}]))
  (fn [images _] images))

(rf/reg-sub ::domain-meeting-images
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entities {:file/category          :file.category/meeting-image
                                        :domain/authority-group group-id}]))
  identity)

(rf/reg-sub ::verdict-images
  (fn [[_ authority-group-id]]
    [(rf/subscribe [::editable-verdict-images authority-group-id])
     (rf/subscribe [::entities/entity authority-group-id])])
  (fn [[images group] _]
    (concat images (some-> group group-model/header-image-for-template vector))))

(rf/reg-sub ::meeting-agenda-images
  (fn [[_ group-id]]
    [(rf/subscribe [::domain-meeting-images group-id])
     (rf/subscribe [::entities/entity group-id])])
  (fn [[images group] _]
    (concat images
            (some-> group group-model/header-image-for-template vector))))

(rf/reg-sub ::declaration-configs
  :<- [::group-id]
  :<- [::entities/context]
  (fn [[group-id ctx] _]
    (->> (entities/find-entities ctx {:entity/parent group-id
                                      :entity/type   :entity.type/declaration-config})
         (sort-by :declaration-config/name))))

(rf/reg-sub ::permit-type-configs
  :<- [::group]
  (fn [group _]
    (->> group
         group-model/permit-type-configs
         (sort-by :permit-type-config/permit-type))))

(defn process-arc-field-config [{:keys [:db/id] :as archiving-field}]
  (if id
    (dissoc archiving-field :entity/type :archiving-field/schema)
    (update archiving-field :archiving-field/schema :db/id)))

(defn process-config-changes
  "For instructions links need to be 'added/fixed' and for archiving-field-configs some key needs to be added or removed
   before passing changes to the backend"
  [changes]
  (->> changes
       (p/remove-vals empty?)
       (p/map-vals
         (fn [vals]
           (-> vals
               (util/safe-update-in [:group.config/archiving-field-configs] #(map process-arc-field-config %)))))))

(rf/reg-event-fx ::fetch-authority-groups
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/authority-groups
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::entities/merge-entity-db]}}))

(rf/reg-event-fx ::fetch-permit-type-configs
  (fn [_ [_ group-id]]
    (let [cmd :group/permit-type-configs]
      {:fx [[:dispatch [::core/set-fetching-config group-id cmd]]
            [:comm/command {:command    cmd
                            :method     :get
                            :data       {:group-id group-id}
                            :on-success [::core/config-fetched group-id :group/permit-type-configs]}]]})))

(rf/reg-event-fx ::fetch-configurable-entities
  (fn [_ [_ group-id cmd config-defaults-kw]]
    {:fx [[:dispatch [::core/set-fetching-config group-id cmd]]
          [:comm/command {:command    cmd
                          :method     :get
                          :data       {:group-id group-id}
                          :on-success [::fetch-configurable-entities.success group-id cmd config-defaults-kw]}]]}))

(rf/reg-event-fx ::fetch-configurable-entities.success
  (fn [{:keys [db]} [_ group-id cmd config-defaults-kw status {:keys [entities] :as resp}]]
    {:db (->> (get resp config-defaults-kw)
              (assoc-in db [::config-defaults config-defaults-kw]))
     :fx [[:dispatch [::core/config-fetched group-id cmd status entities]]]}))

(rf/reg-event-fx ::fetch-attachment-types-in-use
  (fn [_ [_ group-id]]
    {:dispatch [::fetch-configurable-entities group-id :group/attachment-types :types-in-use]}))

(rf/reg-event-fx ::fetch-domain-attachment-types
  (fn [_ [_ group-id]]
    {:dispatch [::fetch-configurable-entities group-id :group/domain-attachment-types :available-attachment-types]}))

(rf/reg-event-fx ::fetch-party-roles.success
  (fn [{:keys [db]} [_ group-id status {:keys [trades] :as resp}]]
    {:fx [[:dispatch [::fetch-configurable-entities.success group-id :group/party-roles :roles-in-use status resp]]]
     :db (-> db
             (assoc-in [::config-defaults :trades] trades))}))

(rf/reg-event-fx ::fetch-party-roles
  (fn [_ [_ group-id]]
    {:fx [[:dispatch [::core/set-fetching-config group-id :group/party-roles]]
          [:comm/command {:command    :group/party-roles
                          :method     :get
                          :data       {:group-id group-id}
                          :on-success [::fetch-party-roles.success group-id]}]]}))

(rf/reg-event-fx ::fetch-closure-categories.success
  (fn [{:keys [db]} [_ group-id status {:keys [closure-categories-in-use] :as resp}]]
    {:fx [[:dispatch [::fetch-configurable-entities.success group-id :group.config/close-without-inspection-categories :closure-categories status resp]]]
     :db (-> db
             (assoc-in [::config-defaults :closure-categories-in-use] closure-categories-in-use))}))

(rf/reg-event-fx ::fetch-closure-categories
  (fn [_ [_ group-id]]
    {:fx [[:dispatch [::core/set-fetching-config group-id :group/closure-categories]]
          [:comm/command {:command    :group/closure-categories
                          :method     :get
                          :data       {:group-id group-id}
                          :on-success [::fetch-closure-categories.success group-id]}]]}))

(rf/reg-event-fx ::fetch-fee-schedules.success
  (fn [_ [_ group-id status {:keys [fee-schedules]}]]
    {:fx [[:dispatch [::core/config-fetched group-id :fee/get-fee-schedules status (or fee-schedules {})]]]}))

(rf/reg-event-fx ::fetch-fee-schedules
  (fn [_ [_ group-id]]
    {:fx [[:dispatch [::core/set-fetching-config group-id :fee/get-fee-schedules]]
          [:comm/command {:command    :fee/get-fee-schedules
                          :method     :get
                          :data       {:authority-group-id group-id}
                          :on-success [::fetch-fee-schedules.success group-id]}]]}))

(rf/reg-event-fx ::fetch-phase-states.success
  (fn [{:keys [db]} [_ group-id status resp]]
    {:db (assoc-in db [::config-defaults :phase-states] resp)
     :fx [[:dispatch [::core/config-fetched group-id :group/phase-states status (or (str-util/blank->nil resp) {})]]]}))

(rf/reg-event-fx ::fetch-phase-states
  (fn [_ [_ group-id]]
    {:fx [[:dispatch [::core/set-fetching-config group-id :group/phase-states]]
          [:comm/command {:command    :group/phase-states
                          :method     :get
                          :data       {:group-id group-id}
                          :on-success [::fetch-phase-states.success group-id]}]]}))

(rf/reg-event-fx ::set-permit-type-config.success
  (fn [_ [_ ptc-id status resp]]
    {:dispatch-n [[::entities/override-entity-db status resp]
                  [:notifications/add :toast :success :success ""]
                  [::clear-ptc-edits ptc-id]]}))

(rf/reg-event-fx ::set-permit-type-config
  (fn [_ [_ data]]
    {:fx [[:dispatch [:comm/command-monitored {:command    :group/set-permit-type-config
                                               :method     :post
                                               :data       data
                                               :on-success [::set-permit-type-config.success (:permit-type-config-id data)]}]]]}))

(rf/reg-event-fx ::save-settings.success
  (fn [_ [_ group-id ptc-id status resp]]
    {:dispatch-n [[::entities/apply-changes status resp]
                  [::clear-ptc-edits ptc-id]
                  [::fetch-phase-states group-id]]}))

(rf/reg-event-fx ::save-ptc-group-config-edits
  (fn [_ [_ group-id ptc-id config-ids->config-settings]]
    {:fx [[:dispatch [:comm/command-monitored {:command    :group/set-group-configs
                                               :method     :post
                                               :data       {:group-id       group-id
                                                            :config-changes (process-config-changes config-ids->config-settings)}
                                               :notify     {:success :changes-saved}
                                               :on-success [::save-settings.success group-id ptc-id]}]]]}))

(rf/reg-sub ::config-defaults :-> ::config-defaults)

(rf/reg-sub ::has-permit-type?
  :<- [::group-permit-types]
  (fn [permit-types [_ expected-permit-types]]
    (-> (set/intersection (set permit-types) expected-permit-types)
        empty?
        not)))

(rf/reg-sub ::types-in-use
  :<- [::config-defaults]
  #(:types-in-use % #{}))

(rf/reg-sub ::roles-in-use
  :<- [::config-defaults]
  #(:roles-in-use % #{}))

(rf/reg-sub ::phase-states
  :<- [::config-defaults]
  :-> :phase-states)

(rf/reg-sub ::domain-attachment-types
  :<- [::group]
  :<- [::entities/context]
  (fn [[group ctx]]
    (let [auth-groups (-> (group-model/descendant-authority-groups group) du/entity-id-set)]
      (->> (entities/find-entities ctx {:entity/type :entity.type/attachment-type})
           (filter #(contains? auth-groups (-> % :attachment-type/authority-group :db/id)))))))

(rf/reg-sub ::party-roles
  :<- [::group-id]
  (fn [group-id _]
    @(rf/subscribe [::group-subs/available-party-roles group-id])))

(rf/reg-sub ::supplemental-permit-type-root-configs
  :<- [::entities/context]
  :<- [::group-id]
  (fn [[ctx group-id] [_ main-permit-type]]
    (->> (get ptc-model/additional-permit-types main-permit-type)
         (map #(entities/find-entity ctx {:group.config/group  group-id
                                          :group.config/target %})))))

(def group-config-settings-in-permit-type-config-editor
  "Group config attributes that are editable in the permit type config editor (why..?)"
  [:group.config/pre-consultation
   :group.config/archive-on-finish
   :group.config/authority-creates-application?
   :group.config/default-work-type-gc
   :group.config/default-work-target-gc
   :group.config/default-change-prevented?
   :group.config/instructions-text-for-phases
   :group.config/neighbour-radius
   :group.config/project-types
   :domain/state])

(rf/reg-sub ::group-config-settings-for-ptc-editor
  (fn [[_ permit-type]]
    (rf/subscribe [::root-group-config permit-type]))
  (fn [root-config _]
    (->> root-config
         ((child/child-entities-of-type-fn :entity.type/group-config))
         (reduce (fn [acc config]
                   (assoc acc
                          (:db/id config)
                          (-> (select-keys config group-config-settings-in-permit-type-config-editor)
                              (update :group.config/default-work-type-gc :db/id)
                              (update :group.config/default-work-target-gc :db/id))))
                 {}))))

(rf/reg-sub ::all-project-types
  :<- [::front-config/config]
  :<- [:tr]
  (fn [[config tr] _]
    (->> config
         :all-project-types
         (sort-by #(tr [%])))))

(rf/reg-sub ::default-category-description
  :<- [:tr/quiet]
  (fn [tr-quiet [_ category]]
    (let [rows (str/split (or (tr-quiet [(keyword "category.description" (name category))])
                              "")
                          #"\n")]
      (->> rows
           (map rt/html-paragraph)
           (str/join "")))))

(rf/reg-event-db ::set-ptc-edit-val
  (fn [db [_ ptc-id k v]]
    (assoc-in db [::permit-type-config-edits ptc-id k] v)))

(rf/reg-event-db ::update-ptc-group-config-changes
  (fn [db [_ ptc-id config-id update-fn]]
    (let [path   [::permit-type-config-edits ptc-id :group-config-settings config-id]
          new-db (update-in db path update-fn)]
      (cond-> new-db
        (empty? (get-in new-db path)) (p/dissoc-in path)))))

(rf/reg-event-db ::clear-ptc-edits
  (fn [db [_ ptc-id]]
    (update-in db
               [::permit-type-config-edits ptc-id]
               (fn [ptc-edits]
                 (-> (apply dissoc ptc-edits permit-type-config-editable-keys)
                     ; Version is kept to help reset rich text editors
                     (update :version (fnil inc 0)))))))

(rf/reg-sub ::permit-type-config-edits
  (fn [db [_ ptc-id]]
    (get-in db [::permit-type-config-edits ptc-id])))

(rf/reg-event-fx ::save-ptc-edits
  (fn [{:keys [db]} [_ group-id ptc-id]]
    (let [{:keys [group-config-settings
                  permit-type-descriptions]} (get-in db [::permit-type-config-edits ptc-id])
          ptc-update?                        permit-type-descriptions]
      {:dispatch-n (cond-> []
                     ptc-update? (conj [::set-permit-type-config
                                        {:group-id                                    group-id
                                         :permit-type-config-id                       ptc-id
                                         :permit-type-config/permit-type-descriptions permit-type-descriptions}])
                     group-config-settings (conj [::save-ptc-group-config-edits
                                                  group-id
                                                  ptc-id
                                                  group-config-settings]))})))

(rf/reg-sub ::saving-ptc-edits?
  :<- [:comm/command-processing? :group/set-permit-type-config]
  :<- [:comm/command-processing? :group/set-group-configs]
  :-> (fn [[ptc? gc?]]
        (or ptc? gc?)))

(rf/reg-event-fx ::end-edit-attachment-type
  (fn [_ [_ id language {:keys [name]}]]
    (let [name (get name language)]
      {:dispatch [::update-command id name language]})))

(rf/reg-event-fx ::store-new-type.success
  (fn [{:keys [db]} [_ conf-id on-done status response]]
    (on-done)
    {:db         (-> db
                     (update ::creating-type #(disj % conf-id)))
     :dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :attachment-type-editor/add-success]]}))

(rf/reg-event-fx ::store-new-type.done
  (fn [{:keys [db]} [_ conf-id status body request-id]]
    {:db       (-> db
                   (update ::creating-type #(disj % conf-id)))
     :dispatch (when (>= status 400)
                 (comm/error-notification-evt status body request-id))}))

(rf/reg-event-fx ::update-type.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/merge-entity-db status response]
                  [:notifications/add :toast :success :success :attachment-type-editor/update-success]]}))

(rf/reg-event-fx ::remove-type.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :attachment-type-editor/remove-success]]}))

(rf/reg-event-fx ::store-new-type
  (fn [{:keys [db]} [_ save-id group-id {:keys [names on-done kind]}]]
    {:db           (update db ::creating-type #(conj (or % #{}) save-id))
     :comm/command {:command    :group/add-attachment-type
                    :method     :post
                    :data       (util/mmap group-id names kind)
                    :on-success [::store-new-type.success save-id on-done]
                    :on-done    [::store-new-type.done save-id]}}))

(rf/reg-sub ::creating-type
  (fn [db _]
    (::creating-type db #{})))

(rf/reg-sub ::create-type-in-progress?
  :<- [::creating-type]
  (fn [creating [_ config-id]] (contains? creating config-id)))

(rf/reg-event-fx ::update-command
  (fn [_ [_ id value language]]
    {:comm/command {:command    :group/rename-attachment-type
                    :method     :post
                    :data       {:type-id  id
                                 :language language
                                 :name     value}
                    :on-success [::update-type.success]}}))

(rf/reg-event-fx ::remove-attachment-type
  (fn [_ [_ id]]
    {:comm/command {:command    :group/remove-attachment-type
                    :method     :post
                    :data       {:type-id id}
                    :on-success [::remove-type.success]}}))

(rf/reg-sub ::avail-attach-edit
  (fn [db [_ config-id]]
    (-> db ::avail-attach-edit (get config-id))))

(rf/reg-sub ::allowed-locales
  :<- [::group]
  i18n/group-locales-handler)

(rf/reg-sub ::code-enforcement?
  :<- [::group-permit-types]
  (fn [permit-types _]
    (contains? permit-types :permit-type/CE)))

(rf/reg-event-fx ::fetch-management-views
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/management-views
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::fetch-management-views.success group-id]}}))

(rf/reg-event-db ::fetch-management-views.success
  (fn [db [_ group-id _ response]]
    (assoc-in db [::management-views-data group-id] response)))

(rf/reg-sub ::all-management-views :-> ::management-views-data)

(rf/reg-sub ::management-views
  :<- [::all-management-views]
  :<- [::group-id]
  :<- [:tr]
  (fn [[all-views group-id tr] [_ group-id-arg]]
    (let [{:keys [management-views primary-permit-type]} (get all-views (or group-id-arg group-id))
          tr                                             (i18n-model/wrap-tr-fn
                                                           (get i18n-model/permit-type-specific-keys primary-permit-type) tr)]
      (->> management-views
           (map (juxt identity #(tr [%])))
           (sort-by last)))))

(rf/reg-sub ::process-flows
  :<- [:tr]
  :<- [::front-config/country]
  (fn [[tr country] [_ permit-type]]
    (->> (group-model/process-flows-for-country country permit-type)
         (sort-by #(tr [%])))))

(rf/reg-sub ::group-session
  :<- [::group-id]
  :<- [:session/authority-groups]
  (fn [[group-id authority-groups] _]
    (p/find-first authority-groups {:db/id group-id})))

(rf/reg-sub ::is-admin?
  :<- [::group-session]
  :<- [:session/admin?]
  (fn [[group-session system-admin?] _]
    (or (:group-admin? group-session) system-admin?)))

(rf/reg-event-fx ::fetch-additional-config
  (fn [_ [_ group-id]]
    {:fx [[:dispatch [::core/set-fetching-config group-id :group/additional-config]]
          [:comm/command {:command    :group/additional-config
                          :method     :get
                          :data       {:group-id group-id}
                          :on-success [::fetch-additional-config.success group-id]}]]}))

(rf/reg-event-fx ::fetch-additional-config.success
  (fn [{:keys [db]} [_ group-id _ {:keys [groups-sharing-municipalities]}]]
    {:fx [[:dispatch [::core/clear-fetching-config group-id :group/additional-config]]]
     :db (assoc db ::additional-config (util/mmap groups-sharing-municipalities))}))

(rf/reg-event-db ::clean-additional-config
  (fn [db _]
    (dissoc db ::additional-config)))

(rf/reg-sub ::additional-config :-> ::additional-config)

(rf/reg-sub ::groups-sharing-municipalities
  :<- [::additional-config]
  :-> :groups-sharing-municipalities)


(rf/reg-sub ::permit-type-supports-grace-period?
  (fn [[_ _permit-type]]
    [(rf/subscribe [:core.front.feature-flags/enabled? :feature-flag.id/grace-period?])])
  (fn [[grace-period-feature-enabled?] [_ permit-type]]
    ;; Use the exact same logic as admin config for determining which permit types support grace period
    ;; L and CR: Grace period in application phase
    ;; CE: Grace period in execution phase
    (and grace-period-feature-enabled?
         (#{:permit-type/L :permit-type/CR :permit-type/CE} permit-type))))
